import { useState, useEffect, useMemo, useRef } from 'react';
import { contractorAxios } from '../pages/ContractorPortal';
import {
  CheckCircle2, Clock, Circle, MapPin, Calendar, CheckSquare, Loader2, MessageSquare, RotateCcw,
  ChevronDown, ChevronUp, Building2, User, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import PhotoCaptureField from './PhotoCaptureField';
import PunchTimeline from './PunchTimeline';
import EntityThread from './EntityThread';
import PunchPhotoTile from './PunchPhotoTile';
import { PUNCH_BTN_PRIMARY, PUNCH_BTN_PRIMARY_SUCCESS, PUNCH_BTN_SECONDARY, PUNCH_BTN_WARN } from '../utils/punchButtons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { thumbUrl, onThumbError } from '../utils/thumbUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  medium: { label: 'Medium', color: 'bg-yellow-500' },
  low: { label: 'Low', color: 'bg-green-500' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700', icon: CheckCircle2 },
  verified: { label: 'Verified', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700', icon: CheckSquare },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

// Wave 5.9 Build 10 — Portal punch list. No dialogs; cards collapse
// in-place with inline Timeline + Thread. Incomplete items sort to top.
export default function ContractorPunchList({ contractorId, contractorEmail, darkMode }) {
  const [observedDark, setObservedDark] = useState(() => {
    if (typeof darkMode === 'boolean') return darkMode;
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    if (typeof darkMode === 'boolean') { setObservedDark(darkMode); return; }
    const obs = new MutationObserver(() => setObservedDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [darkMode]);
  const isDark = observedDark;

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [completeItem, setCompleteItem] = useState(null);
  const [completeComment, setCompleteComment] = useState('');
  const [completeAfterPhoto, setCompleteAfterPhoto] = useState(null);
  const [completeAfterPreview, setCompleteAfterPreview] = useState('');
  const [completing, setCompleting] = useState(false);
  const [undoing, setUndoing] = useState({});
  const [deletingPhoto, setDeletingPhoto] = useState({});
  const viewedRef = useRef(new Set());

  const fetchPunchList = async () => {
    try {
      setLoading(true);
      const { data } = await contractorAxios.get(`${API}/contractor-portal/punch-list`);
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Failed to fetch punch list:', error);
      toast.error('Failed to load punch list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPunchList(); }, [contractorId, contractorEmail]);

  // Stamp first_viewed_at once per item (best-effort, idempotent).
  useEffect(() => {
    if (!items?.length) return;
    items.forEach((it) => {
      if (!it?.id || it.first_viewed_at) return;
      if (viewedRef.current.has(it.id)) return;
      viewedRef.current.add(it.id);
      contractorAxios.post(`${API}/contractor-portal/punch-list/${it.id}/view`).catch(() => {});
    });
  }, [items]);

  // Sort: incomplete first (open, in_progress), then completed, then verified.
  // Within a bucket, newest first by updated_at.
  const sortedItems = useMemo(() => {
    const rank = (s) => (s === 'open' || s === 'in_progress' ? 0 : s === 'completed' ? 1 : 2);
    return [...items].sort((a, b) => {
      const dr = rank(a.status) - rank(b.status);
      if (dr) return dr;
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [items]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const acknowledgeItem = async (itemId) => {
    try {
      await contractorAxios.post(`${API}/contractor-portal/punch-list/${itemId}/acknowledge`, {});
      toast.success('Item acknowledged');
      fetchPunchList();
    } catch {
      toast.error('Failed to acknowledge item');
    }
  };

  const openCompleteDialog = (item) => {
    setCompleteItem(item);
    setCompleteComment('');
    setCompleteAfterPhoto(null);
    setCompleteAfterPreview('');
  };
  const closeCompleteDialog = () => {
    if (completing) return;
    setCompleteItem(null);
    setCompleteComment('');
    setCompleteAfterPhoto(null);
    setCompleteAfterPreview('');
  };
  const doComplete = async () => {
    if (!completeItem) return;
    const comment = completeComment.trim();
    if (!comment) { toast.error('Add a comment — required for completion.'); return; }
    setCompleting(true);
    try {
      if (completeAfterPhoto) {
        const fd = new FormData();
        fd.append('file', completeAfterPhoto);
        fd.append('caption', comment.slice(0, 200));
        await contractorAxios.post(
          `${API}/contractor-portal/punch-list/${completeItem.id}/after-photo`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      await contractorAxios.post(
        `${API}/contractor-portal/punch-list/${completeItem.id}/complete`,
        { comment }
      );
      toast.success('Item marked complete.');
      setCompleteItem(null);
      setCompleteComment('');
      setCompleteAfterPhoto(null);
      setCompleteAfterPreview('');
      fetchPunchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark complete.');
    } finally {
      setCompleting(false);
    }
  };

  const undoComplete = async (item) => {
    if (!item?.id) return;
    setUndoing((d) => ({ ...d, [item.id]: true }));
    try {
      const { data } = await contractorAxios.post(
        `${API}/contractor-portal/punch-list/${item.id}/undo-complete`,
        { comment: '' }
      );
      toast.success('Completion undone. Timeline updated.');
      if (data?.item) {
        setItems((prev) => prev.map((x) => (x.id === item.id ? data.item : x)));
      } else {
        fetchPunchList();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not undo.');
    } finally {
      setUndoing((d) => { const n = { ...d }; delete n[item.id]; return n; });
    }
  };

  const deleteItemPhoto = async (item, photo) => {
    const key = `${item.id}:${photo.id}`;
    if (!photo?.id) { toast.error('Photo has no id — legacy record'); return; }
    if (!window.confirm('Delete this photo? An audit entry will be appended.')) return;
    setDeletingPhoto((d) => ({ ...d, [key]: true }));
    try {
      await contractorAxios.delete(`${API}/contractor-portal/punch-list/${item.id}/photos/${photo.id}`);
      setItems((prev) => prev.map((x) => (x.id === item.id
        ? { ...x, photos: (x.photos || []).filter((p) => p.id !== photo.id) }
        : x)));
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete photo');
    } finally {
      setDeletingPhoto((d) => { const n = { ...d }; delete n[key]; return n; });
    }
  };

  // Theme tokens.
  const cardBg = isDark ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-slate-200';
  const cardText = isDark ? 'text-slate-100' : 'text-slate-800';
  const mutedText = isDark ? 'text-slate-400' : 'text-slate-500';
  const dimText = isDark ? 'text-slate-500' : 'text-slate-400';

  const PriorityBadge = ({ priority }) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
    return <Badge className={`${config.color} text-white text-[10px] px-1.5`}>{config.label}</Badge>;
  };
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} text-[10px] px-1.5 gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`text-center py-12 ${mutedText}`}>
        <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium">No punch list items assigned</p>
        <p className="text-sm">Items assigned to you will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`flex items-center gap-4 text-xs mb-4 ${mutedText}`} data-testid="portal-punch-stats">
        <span><strong className={cardText}>{stats.total}</strong> Total</span>
        <span><strong className="text-red-500 dark:text-red-400">{stats.open}</strong> Open</span>
        <span><strong className="text-blue-500 dark:text-blue-400">{stats.in_progress}</strong> In Progress</span>
        <span><strong className="text-green-500 dark:text-green-400">{stats.completed}</strong> Completed</span>
      </div>

      <div className="space-y-3">
        {sortedItems.map(item => {
          const isExpanded = expandedIds.has(item.id);
          const firstPhoto = (() => {
            const p = (item.photos || [])[0];
            if (!p) return null;
            return typeof p === 'string' ? { url: p, type: '' } : p;
          })();
          const heroBorder = isDark ? 'border-[#3A3A3A]' : 'border-slate-200';
          return (
            <div
              key={item.id}
              className={`${cardBg} border rounded-lg transition-colors`}
              data-testid={`portal-punch-card-${item.id}`}
            >
              {/* Collapsed header row — click anywhere to toggle. */}
              <button
                type="button"
                onClick={() => toggleExpand(item.id)}
                className="w-full flex items-start gap-3 p-3 text-left"
                aria-expanded={isExpanded}
                data-testid={`portal-punch-toggle-${item.id}`}
              >
                {(firstPhoto?.url || firstPhoto?.thumb_url) && (
                  <div className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border ${heroBorder} bg-slate-100 dark:bg-[#0D0D0D]`}>
                    <img
                      src={thumbUrl(firstPhoto, 160)}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={onThumbError(firstPhoto)}
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusBadge status={item.status} />
                    <PriorityBadge priority={item.priority} />
                    {item.contractor_acknowledged && (
                      <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 text-[10px] border border-green-500/30">
                        Acknowledged
                      </Badge>
                    )}
                  </div>
                  <div className={`font-medium ${cardText}`}>{item.title}</div>
                  <div className={`flex items-center gap-3 text-xs mt-1 ${dimText} flex-wrap`}>
                    {item.assigned_to_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {item.assigned_to_name}
                      </span>
                    )}
                    {item.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.location}
                      </span>
                    )}
                    {item.due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(item.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`ml-2 mt-1 ${dimText}`}>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>

              {/* Expanded body — inline photo + timeline + thread. */}
              {isExpanded && (
                <div
                  className={`px-3 pb-3 border-t ${isDark ? 'border-[#2A2A2A]' : 'border-slate-200'}`}
                  data-testid={`portal-punch-expanded-${item.id}`}
                >
                  <div className={`text-xs mt-2 ${mutedText}`}>
                    {item.created_by_name && (
                      <span>Created by <strong className={cardText}>{item.created_by_name}</strong>
                        {item.created_at ? ` on ${fmtDate(item.created_at)}` : ''}
                      </span>
                    )}
                    {item.project_name && (
                      <span className="flex items-center gap-1 mt-1">
                        <Building2 className="w-3 h-3" /> {item.project_name}
                      </span>
                    )}
                  </div>

                  {item.description && (
                    <p className={`text-sm mt-2 ${mutedText}`}>{item.description}</p>
                  )}

                  {/* Photos grid — Build 11 shared PunchPhotoTile: corner
                      chip label (not full-bar), caption under image,
                      editable label + delete for the poster until verify. */}
                  {item.photos && item.photos.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {item.photos.map((raw, idx) => {
                        const p = typeof raw === 'string' ? { url: raw } : raw;
                        if (!p?.url) return null;
                        const mine = !!p.id && p.uploaded_by === contractorId;
                        const canDelete = !item.verified_at && mine;
                        const canEditLabel = !item.verified_at && mine;
                        return (
                          <PunchPhotoTile
                            key={p.id || `${idx}-${p.url}`}
                            photo={p}
                            darkMode={isDark}
                            canDelete={canDelete}
                            canEditLabel={canEditLabel}
                            testIdBase={`portal-punch-photo-${item.id}-${idx}`}
                            onDelete={() => deleteItemPhoto(item, p)}
                            onSaveLabel={async (newLabel) => {
                              try {
                                await contractorAxios.patch(
                                  `${API}/contractor-portal/punch-list/${item.id}/photos/${p.id}`,
                                  { label: newLabel }
                                );
                                setItems((prev) => prev.map((x) => (x.id === item.id
                                  ? { ...x, photos: (x.photos || []).map((pp) => (pp.id === p.id ? { ...pp, label: newLabel, label_edited: true } : pp)) }
                                  : x)));
                                toast.success('Label updated');
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Could not save label');
                              }
                            }}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Actions row. */}
                  <div className={`flex flex-wrap items-center gap-2 mt-3 pt-3 border-t ${isDark ? 'border-[#2A2A2A]' : 'border-slate-200'}`}>
                    {!item.contractor_acknowledged && item.status === 'open' && (
                      <button
                        type="button"
                        onClick={() => acknowledgeItem(item.id)}
                        className={PUNCH_BTN_PRIMARY}
                        data-testid={`portal-punch-acknowledge-${item.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Acknowledge
                      </button>
                    )}
                    {item.contractor_acknowledged && item.status !== 'completed' && item.status !== 'verified' && (
                      <button
                        type="button"
                        onClick={() => openCompleteDialog(item)}
                        className={PUNCH_BTN_PRIMARY_SUCCESS}
                        data-testid={`portal-punch-mark-complete-${item.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Mark complete
                      </button>
                    )}
                    {item.status === 'completed' && !item.verified_at && (
                      <button
                        type="button"
                        onClick={() => undoComplete(item)}
                        disabled={!!undoing[item.id]}
                        className={PUNCH_BTN_WARN}
                        data-testid={`portal-punch-undo-complete-${item.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {undoing[item.id] ? 'Undoing…' : 'Undo Complete'}
                      </button>
                    )}
                    {item.status === 'completed' && (
                      <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Completed — awaiting verification
                      </span>
                    )}
                    {item.status === 'verified' && (
                      <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                        <CheckSquare className="w-3.5 h-3.5" />
                        Verified &amp; closed
                      </span>
                    )}
                  </div>

                  {/* Inline Timeline. */}
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-[#2A2A2A]' : 'border-slate-200'}`}>
                    <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${mutedText}`}>Timeline</div>
                    <PunchTimeline item={item} darkMode={isDark} />
                  </div>

                  {/* Inline Thread — beside/below via responsive layout at parent level. */}
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-[#2A2A2A]' : 'border-slate-200'}`}>
                    <EntityThread
                      entityType="punch_item"
                      entityId={item.id}
                      party="contractor"
                      axiosInstance={contractorAxios}
                      canPost={item.status !== 'verified'}
                      canDeletePhotos={!item.verified_at}
                      currentUserId={contractorId}
                      isDesigner={false}
                      entityVerified={!!item.verified_at}
                      darkMode={isDark}
                      testIdPrefix={`portal-punch-thread-${item.id}`}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mark Complete dialog remains — this is a form gate, not a comment popup. */}
      <Dialog open={!!completeItem} onOpenChange={(o) => !o && closeCompleteDialog()}>
        <DialogContent
          className={`max-w-md ${isDark ? 'bg-[#1A1A1A] border-[#2A2A2A] text-slate-100' : 'bg-white border-slate-200 text-slate-900'}`}
          data-testid="portal-punch-complete-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base leading-tight">
              Mark complete
              <div className={`text-xs font-normal mt-1 line-clamp-2 ${mutedText}`}>{completeItem?.title}</div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                Comment <span className="text-red-500">*</span>
              </label>
              <p className={`text-[11px] mt-0.5 mb-1 ${mutedText}`}>Describe what you did. This lands in the item timeline.</p>
              <Textarea
                value={completeComment}
                onChange={(e) => setCompleteComment(e.target.value)}
                placeholder="e.g. Repaired drywall crack, sanded and painted to match. Ready for review."
                rows={4}
                className={`text-sm min-h-[100px] ${isDark ? 'bg-[#0D0D0D] border-[#3A3A3A] text-slate-100' : ''}`}
                data-testid="portal-punch-complete-comment"
                autoFocus
              />
            </div>

            <div>
              <label className={`text-xs font-semibold uppercase tracking-wide block mb-1 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                After photo <span className={`font-normal normal-case ${dimText}`}>(optional but recommended)</span>
              </label>
              <PhotoCaptureField
                preview={completeAfterPreview}
                onFile={(file) => { setCompleteAfterPhoto(file); setCompleteAfterPreview(URL.createObjectURL(file)); }}
                onClear={() => { setCompleteAfterPhoto(null); setCompleteAfterPreview(''); }}
                disabled={completing}
                compact
                darkMode={isDark}
                testIdBase="portal-punch-complete-photo"
                takeLabel="Take after photo"
                libraryLabel="Choose existing"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={doComplete}
              disabled={completing || !completeComment.trim()}
              className="w-full min-h-[52px] bg-green-600 hover:bg-green-700 gap-2 text-base font-semibold text-white"
              data-testid="portal-punch-complete-submit"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              {completing ? 'Submitting…' : 'Mark complete'}
            </Button>
            <Button variant="ghost" onClick={closeCompleteDialog} disabled={completing} className={`w-full ${mutedText}`}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
